/* ==========================================================================
   LED by CHEVAL — Treatment guide (behaviour only)
   --------------------------------------------------------------------------
   Every user-facing string is server-rendered in
   sections/lbc-treatment-guide.liquid. This file:
   · toggles the `hidden` attribute on screens and result fragments,
   · tracks the answers,
   · runs the scoring (weights and tie order verbatim from the source
     design "LED Treatment Guide.html"),
   · fills <span data-token> placeholders with values (the email address),
   · posts a client event to Klaviyo on submit, and the subscribe call only
     when the marketing box is ticked.
   It never writes copy or markup. With the script absent the
   card shows its intro screen and the rest of the page is untouched.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-guide-root]');
  if (!root) return;

  var QUESTIONS = ['noticed', 'area', 'horses', 'access'];
  var TIE_ORDER = ['stifle', 'w108', 'pad', 'w72', 'sheet'];
  var ELIGIBLE = {
    neck: ['w72', 'w108', 'pad', 'sheet'],
    chest: ['w72', 'w108', 'stifle', 'pad'],
    topline: ['w72', 'w108', 'stifle', 'pad', 'sheet'],
    hind: ['sheet'],
    stifle: ['stifle'],
    legs: ['w72', 'w108'],
    multi: ['w72', 'w108', 'stifle', 'pad', 'sheet']
  };

  var state = { step: -1, answers: {} };

  /* --- helpers ------------------------------------------------------------ */
  function one(selector, context) {
    return (context || root).querySelector(selector);
  }

  function all(selector, context) {
    return Array.prototype.slice.call((context || root).querySelectorAll(selector));
  }

  function hide(node) {
    node.hidden = true;
  }

  /* Show the node whose `attr` equals `value`, hide its siblings; return it. */
  function showOnly(nodes, attr, value) {
    var shown = null;
    nodes.forEach(function (node) {
      var on = node.getAttribute(attr) === value;
      node.hidden = !on;
      if (on) shown = node;
    });
    return shown;
  }

  /* --- scoring ------------------------------------------------------------
     Weights from the source design, with two changes agreed 2026-09-13:
     topline gives Pad +2 (was +3), and when Pad or Sheet is recommended the
     alternative is always Wrap108. */
  function score(a) {
    var area = a.area || 'multi';
    var s = { w72: 0, w108: 0, stifle: 0, pad: 0, sheet: 0 };
    if (area === 'stifle') s.stifle += 6;
    if (area === 'hind') s.sheet += 6;
    if (area === 'legs') s.w72 += 3;
    if (['neck', 'chest', 'topline'].indexOf(area) > -1) s.w108 += 2;
    if (area === 'topline') s.pad += 2;
    if (['chest', 'topline', 'multi'].indexOf(area) > -1) s.stifle += 1;
    if (area === 'multi') { s.w108 += 3; s.sheet += 1; }
    if (a.noticed === 'soreness') s.w72 += 2;
    if (a.noticed === 'upkeep') s.pad += 1;
    if (a.horses === 'one') s.w72 += 1;
    if (a.horses === 'few') s.pad += 2;
    if (a.horses === 'yard') s.pad += 6;

    var ranked = ELIGIBLE[area].slice().sort(function (x, y) {
      return (s[y] - s[x]) || (TIE_ORDER.indexOf(x) - TIE_ORDER.indexOf(y));
    });
    var sole = ranked.length < 2;
    var rec = ranked[0];
    var alt = sole ? 'w108' : ranked[1];
    if (rec === 'pad' || rec === 'sheet') alt = 'w108';
    return { rec: rec, alt: alt, area: area, soleFit: sole && rec !== 'sheet' };
  }

  /* reasons[system][area] || reasons[system].multi || reasons[system].topline */
  function reasonNode(system, area) {
    return one('[data-guide-reason="' + system + '.' + area + '"]')
      || one('[data-guide-reason="' + system + '.multi"]')
      || one('[data-guide-reason="' + system + '.topline"]');
  }

  function rank(button, primary) {
    if (!button) return;
    button.classList.toggle('lbc-guide__btn--primary', primary);
    button.classList.toggle('lbc-guide__btn--secondary', !primary);
  }

  /* --- result ------------------------------------------------------------- */
  function renderResult() {
    var a = state.answers;
    var r = score(a);
    var isSheet = r.rec === 'sheet';
    var buyFirst = a.access === 'buy';
    var intensive = a.noticed === 'soreness' || a.noticed === 'rehab';

    showOnly(all('[data-guide-name]'), 'data-guide-name', r.rec);

    all('[data-guide-reason]').forEach(hide);
    var reason = reasonNode(r.rec, r.area);
    if (reason) reason.hidden = false;

    showOnly(all('[data-guide-protocol]'), 'data-guide-protocol', intensive ? 'intensive' : 'ongoing');

    /* Rent column: the system's figure where one was rendered for this
       market, otherwise the "not available" status. Sheet never rents. */
    var rentNodes = all('[data-guide-rent]');
    var rent = isSheet ? null : showOnly(rentNodes, 'data-guide-rent', r.rec);
    if (isSheet) rentNodes.forEach(hide);
    var noRent = one('[data-guide-no-rent]');
    if (noRent) noRent.hidden = !!rent;

    showOnly(all('[data-guide-buy]'), 'data-guide-buy', r.rec);

    /* Actions: rental is primary unless the visitor chose to buy, the
       system is the Sheet, or no rental link was rendered. */
    var group = showOnly(all('[data-guide-actions]'), 'data-guide-actions', r.rec);
    if (group) {
      var rental = one('[data-guide-action="rental"]', group);
      var view = one('[data-guide-action="view"]', group);
      var viewFirst = isSheet || buyFirst || !rental;
      group.classList.toggle('lbc-guide__actions--view-first', viewFirst);
      rank(view, viewFirst);
      if (rental) {
        rental.hidden = isSheet;
        rank(rental, !viewFirst);
      }
    }

    showOnly(all('[data-guide-terms]'), 'data-guide-terms', isSheet ? 'sheet' : 'default');

    showOnly(all('[data-guide-alt-name]'), 'data-guide-alt-name', r.alt);
    showOnly(all('[data-guide-alt-price]'), 'data-guide-alt-price', r.alt);
    /* Pad and Sheet carry their own Wrap108 line, rendered only where a
       Wrap108 rental price resolved; elsewhere fall back to the generic
       Wrap108 line. */
    var altReason = r.rec === 'pad' ? 'padRecommended'
      : (isSheet ? 'sheetRecommended' : (r.soleFit ? 'soleFit' : r.alt));
    if (!one('[data-guide-alt-reason="' + altReason + '"]')) altReason = r.alt;
    showOnly(all('[data-guide-alt-reason]'), 'data-guide-alt-reason', altReason);
  }

  /* --- screens ------------------------------------------------------------ */
  var screens = all('[data-guide-screen]');
  var card = one('[data-guide-card]') || root;

  function render() {
    var current = QUESTIONS[state.step];
    screens.forEach(function (screen) {
      var kind = screen.getAttribute('data-guide-screen');
      var on = false;
      if (kind === 'intro') on = state.step === -1;
      else if (kind === 'result') on = state.step >= QUESTIONS.length;
      else if (kind === 'question') on = screen.getAttribute('data-guide-question') === current;
      screen.hidden = !on;
    });

    all('[data-guide-option]').forEach(function (option) {
      var screen = option.closest('[data-guide-question]');
      var key = screen ? screen.getAttribute('data-guide-question') : null;
      var pressed = !!key && state.answers[key] === option.getAttribute('data-guide-option');
      option.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });

    if (state.step >= QUESTIONS.length) renderResult();
  }

  /* Keep the card in view after a step change on small screens. */
  function settle() {
    if (card.getBoundingClientRect().top < 0) {
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  root.addEventListener('click', function (event) {
    var option = event.target.closest('[data-guide-option]');
    if (option && root.contains(option)) {
      var screen = option.closest('[data-guide-question]');
      if (!screen) return;
      state.answers[screen.getAttribute('data-guide-question')] = option.getAttribute('data-guide-option');
      state.step += 1;
      render();
      settle();
      return;
    }

    var go = event.target.closest('[data-guide-go]');
    if (!go || !root.contains(go)) return;
    var what = go.getAttribute('data-guide-go');
    if (what === 'start') state.step = 0;
    if (what === 'back') state.step = Math.max(-1, state.step - 1);
    if (what === 'restart') {
      state.step = -1;
      state.answers = {};
      resetSave();
    }
    render();
    settle();
  });

  /* --- save my result ----------------------------------------------------- */
  var form = one('[data-guide-form]');
  var input = form ? one('input[type="email"]', form) : null;
  var submit = form ? one('button[type="submit"]', form) : null;
  var error = one('[data-guide-error]');
  var saved = one('[data-guide-saved]');
  var savedEmail = saved ? one('[data-token="email"]', saved) : null;

  function resetSave() {
    if (!form) return;
    form.reset();
    form.hidden = false;
    if (submit) submit.disabled = false;
    if (error) error.hidden = true;
    if (saved) saved.hidden = true;
    if (savedEmail) savedEmail.textContent = '';
  }

  var KLAVIYO_CLIENT = 'https://a.klaviyo.com/client/';

  function postJson(key, endpoint, payload) {
    return fetch(KLAVIYO_CLIENT + endpoint + '/?company_id=' + encodeURIComponent(key), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2025-07-15' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) throw new Error(String(response.status));
    });
  }

  function guideProperties() {
    var r = score(state.answers);
    var nameNode = one('[data-guide-name="' + r.rec + '"]');
    return {
      guide_system: nameNode ? nameNode.textContent.trim() : r.rec,
      guide_area: state.answers.area || '',
      guide_noticed: state.answers.noticed || '',
      guide_horses: state.answers.horses || '',
      guide_access: state.answers.access || ''
    };
  }

  /* The result trigger: a client event on every submit, no consent needed. */
  function sendEvent(key, email, props) {
    return postJson(key, 'events', {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: root.getAttribute('data-guide-metric') || '' } } },
          profile: { data: { type: 'profile', attributes: { email: email, properties: props } } },
          properties: props
        }
      }
    });
  }

  /* The marketing list: only when the box is ticked. */
  function sendSubscribe(key, list, email, props) {
    return postJson(key, 'subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: root.getAttribute('data-guide-source') || '',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                properties: props
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: list } } }
      }
    });
  }

  function subscribe(email, marketing) {
    var key = root.getAttribute('data-guide-klaviyo-key');
    var list = root.getAttribute('data-guide-list-id');
    if (!key || !window.fetch) return Promise.reject(new Error('config'));

    var props = guideProperties();
    return sendEvent(key, email, props).then(function () {
      if (!marketing || !list) return;
      return sendSubscribe(key, list, email, props).catch(function (error) {
        if (window.console) console.error('lbc-treatment-guide subscribe', error);
      });
    });
  }

  if (form && input) {
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var email = (input.value || '').trim();
      if (!email) return;
      var consent = one('input[name="marketing"]', form);
      var marketing = !!(consent && consent.checked);
      if (error) error.hidden = true;
      if (submit) submit.disabled = true;
      form.setAttribute('aria-busy', 'true');
      subscribe(email, marketing).then(function () {
        form.hidden = true;
        if (savedEmail) savedEmail.textContent = email;
        if (saved) saved.hidden = false;
      }, function () {
        if (error) error.hidden = false;
      }).then(function () {
        if (submit) submit.disabled = false;
        form.removeAttribute('aria-busy');
      });
    });
  }

  render();
})();
